namespace app.autenticacao {
    "use strict";
    
    declare const msal;
    
    export interface IPermission {
        id: string;
        context: string;
        readOnly: boolean;
    }
    
    export interface IAuthority {
        authority: string;
    }
    
    export interface IUser {
        usuarioId: number;
        login: string;
        email: string;
        nome: string;
        pessoaId: number;
        lotacao: string;
        componentes: IPermission[];
        contas: IConta[];
        casTGT: string;
        authorities: IAuthority[];
        clientOnly: boolean;
        scope: string[];
    }
    
    export interface IAuthInfo {
        access_token: string;
        token_type: string;
        expires_in: number;
        scope: string;
    }

    export interface IConta {
        id: number;
        codigo: string;
        sigla: string;
        nome: string;
        principal: boolean;
        atual: boolean;
    }

    export class AuthService {

        public USER_QUERY_URL: string;
        public OAUTH_TOKEN_URL: string;
        public OAUTH_REVOKE_TOKEN_URL: string;
        public AUTH_POST_CONTENT_TYPE: string = "application/x-www-form-urlencoded; charset=utf-8";
        public ACCESS_TOKEN = "access_token";
        private user: IUser = null;
        private msalInstance: any = null;
        private msalConfig = {
		    auth: {
		        clientId: this.properties.msalClientId,
		        authority: `https://login.microsoftonline.com/${this.properties.msalTennantId}`,
		        navigateToLoginRequestUrl: true
		    },
		    cache: {
		        cacheLocation: "sessionStorage",
		        storeAuthStateInCookie: true,
		        secureCookies: true,
                claimsBasedCachingEnabled: true
		    }
		};
		private msalScopes = [`${this.properties.msalClientId}/.default`];
		private msalAccount = null;
        
        /** @ngInject **/
        constructor(
            private $http : ng.IHttpService, 
            private $cookies: ng.cookies.ICookiesService, 
            private $httpParamSerializer: ng.IHttpParamSerializer, 
            private $q: ng.IQService, 
            private $rootScope: ng.IScope, 
            private properties: app.support.constants.Properties,
            private $cacheFactory: ng.ICacheFactoryService,
            private $window: ng.IWindowService,
            private instanaService: InstanaService
        ){
            this.USER_QUERY_URL = `${properties.apiUrl}/integracoes-identidades/api/user`;
            this.OAUTH_TOKEN_URL = `${properties.apiUrl}/integracoes-identidades/oauth/token`;
            this.OAUTH_REVOKE_TOKEN_URL = `${properties.apiUrl}/integracoes-identidades/oauth/access-token`;
            this.msalInstance = new msal.PublicClientApplication(this.msalConfig);
            this.msalInstance.initialize();
        }
        
        /**
         * Verifica o tipo de autenticação
         */
        public isLocalAuthentication(): boolean {
			return this.properties.msalClientId === undefined;
		}

        /**
         * Verifica se o usuário já se autenticou no sistema. A verificação aqui checa se o token de acesso previamente 
         * armazenado (durante o login do usuário) ainda está válido no servidor.
         */
        public isAuthenticated(): ng.IPromise<IUser> {
            return this.$http.get(this.USER_QUERY_URL).then((response: ng.IHttpPromiseCallbackArg<IUser>) => {    
                return response.data;
            });
        }
        
        /**
         * Retorna os dados do usuário logado.
         */
        public getUser(useCache: boolean = true): ng.IPromise<IUser> {
            if (this.$cookies.get(this.ACCESS_TOKEN)) {
                let params = {};

                if (!useCache) {
                    params["ts"] = new Date().getTime();
                }
                return this.$http.get<IUser>(this.USER_QUERY_URL, { cache: useCache, params: params }).then((response) => {
                    this.instanaService.registerUser(response.data);
                    return this.user = response.data;
                });
            }
            return this.$q.reject("Usuário ainda não foi carregado!");
        }

        /**
         * Utiliza a API fornecida pelo Oauth2 para solicitar um token de acesso. Utiliza "password"
         * como grant type e Basic Authorization para informar as credenciais do cliente.
         */
        public authenticate(login?: string, password?: string, selectAccount: boolean = false): ng.IPromise<any> {
            this.clear();
            
            if (this.isLocalAuthentication()) {
			    return this.authentication(login, password);
			} else {
				let promise = null;
				
				if (selectAccount) {
					promise = this.msalInstance.loginPopup(
						{scopes: this.msalScopes, prompt: "select_account", forceRefresh: true});
				} else { 
					promise = this.msalInstance.acquireTokenSilent(
						{scopes: this.msalScopes, account: this.msalAccount, forceRefresh: true})
						.then((response: any) => response, 
						 	 (error: any) => {
								  console.error("Silent Error: " + error);
								  return this.msalInstance.acquireTokenPopup(
									  {scopes: this.msalScopes, account: this.msalAccount});
						});
				}
				return promise.then((response: any) => {
					this.msalAccount = response.account;
					
					login = response.account.username;
					password = response.idToken;
					
					return this.authentication(login, password);
				});
			}
        }

        /**
         * Invalida a seção no serviço de autenticação e remove o cookie com o token de acesso.
         */ 
        public logout(): ng.IPromise<void> {
	    	return this.localLogout();
        }
        
        public logoutAll(): ng.IPromise<void> {
	    	if (this.isLocalAuthentication()) {
				return this.localLogout();
			} else {
				const logoutRequest = {
			        logoutHint: this.msalAccount.idTokenClaims.login_hint
		    	};
				return this.localLogout()
					.then(() => this.msalInstance.logoutPopup(logoutRequest));
			}
        }        
        
        public getUserLogin(): string {
            return !!this.user ? this.user.login : null;
        }

        public clearCache(): void {
            let httpCache = this.$cacheFactory.get("$http");
            if (httpCache) {
                httpCache.removeAll();
            }
        }

        public verifyRedirectAndAuthenticate() : ng.IPromise<boolean> {
            return this.msalInstance.handleRedirectPromise()
                .then(response => {
                    if  (!!response) {
                        this.msalAccount = response.account;

                        return this.authentication(response.account.username, response.idToken)
                            .then(() => true);
                    } else {
                        return false;
                    }
                }, (error) => {
                    console.error("Erro ao verificar se houve redirecionamento: " + error);
                    return false;
                });
        }

        /**
         * Nenhum código será executado após a chamada a este método.
         */
        public loginRedirect(): ng.IPromise<void> {
            return this.msalInstance.loginRedirect({
                scopes: this.msalScopes,
                prompt: "select_account"
            });
        }
        
        private clear(): void {
            this.clearCache();
            this.$window.localStorage.removeItem("searchData");
            this.$window.localStorage.removeItem("searchHistoricData");
            
            this.$cookies.remove(this.ACCESS_TOKEN);
            this.user = null;
            this.msalAccount = null;
            this.instanaService.unregisterUser();
        }
        
        private authentication(usuario: string, senha: string): ng.IPromise<IAuthInfo> {
			let data = {username: usuario, password: senha, grant_type: "password"};
            let request = {
                method: "POST",
                url: this.OAUTH_TOKEN_URL,
                headers: {
                    "Authorization": `Basic ${btoa("userinterface:userinterface")}`,
                    "Content-type": this.AUTH_POST_CONTENT_TYPE
                },
                data: this.$httpParamSerializer(data)
            };
            
			return this.$http<IAuthInfo>(request).then(response => {
                let authInfo = response.data;
                this.$cookies.put(this.ACCESS_TOKEN, authInfo.access_token, {path: "/", secure: true});
                this.$rootScope.$broadcast("user:logged");

                // window.clarity("identify", usuario); // Descomentar se o clarity for reativado em algum momento

                return authInfo;
            });
		}
		
		private localLogout() {			
			return this.$http.delete(this.OAUTH_REVOKE_TOKEN_URL)
				.then(() => {
            		this.clear();
            		this.$rootScope.$broadcast("user:exited");
                    this.$window.location.href = "/login";
        		});
		}

    }

    angular
        .module("app.autenticacao")
        .service("authService", AuthService);
}